import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Alert,
  InputGroup,
  InputGroupText,
  CardFooter,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Breadcrumbs from "components/Common/Breadcrumb";
import { fetchPaket, fetchPaketDetail } from '../../../services/Master/MasterPaket';
// Perhatikan: Path import saya sesuaikan dengan kode yang Anda berikan sebelumnya
import paketASN from "../../../assets/images/asnpaket.png"; 
import {
  FiSearch,
  FiAlertCircle,
  FiAward,
  FiZap,
  FiArrowRight,
  FiTag,
  FiFileText,
  FiClock,
  FiArrowLeft
} from 'react-icons/fi';

const PaketDetails = () => {
  document.title = "Daftar Tryout";
  const location = useLocation();
  const navigate = useNavigate();

  const [tryouts, setTryouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [selectedIdJenisSeleksi, setSelectedIdJenisSeleksi] = useState(null);
  const [selectedIdTipeKonten, setSelectedIdTipeKonten] = useState(null);
  const [selectedIdBidang, setSelectedIdBidang] = useState(null);

  const [idJenisSeleksiNotFound, setIdJenisSeleksiNotFound] = useState(false);
  const [idTipeKontenNotFound, setIdTipeKontenNotFound] = useState(false);
  const [idBidangNotFound, setIdBidangNotFound] = useState(false);

  const [noResultsFromSearch, setNoResultsFromSearch] = useState(false);
  const [dataTryoutsEmpty, setDataTryoutsEmpty] = useState(false);

  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  // State untuk modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  // Effect 1: Mengatur parameter dari URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idJenisSeleksiFromUrl = params.get('id_jenis_seleksi');
    const idTipeKontenFromUrl = params.get('id_tipe_konten');
    const idBidangFromUrl = params.get('id_bidang');
    

    const parseAndSetId = (idFromUrl, setIdState, setIdNotFoundState) => {
      if (idFromUrl) {
        const parsedId = parseInt(idFromUrl, 10);
        if (!isNaN(parsedId) && parsedId > 0) {
          setIdState(parsedId);
          setIdNotFoundState(false);
        } else {
          setIdState(null);
          setIdNotFoundState(true);
        }
      } else {
        setIdState(null);
        setIdNotFoundState(false);
      }
    };

    parseAndSetId(idJenisSeleksiFromUrl, setSelectedIdJenisSeleksi, setIdJenisSeleksiNotFound);
    parseAndSetId(idTipeKontenFromUrl, setSelectedIdTipeKonten, setIdTipeKontenNotFound);
    parseAndSetId(idBidangFromUrl, setSelectedIdBidang, setIdBidangNotFound);

    setUrlParamsProcessed(true);
  }, [location.search]);

  // Effect 2: Debounce searchTerm
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Effect 3: Memanggil API saat filter berubah
  useEffect(() => {
    if (!urlParamsProcessed) {
      return;
    }

    if (idJenisSeleksiNotFound || idTipeKontenNotFound) {
      setLoading(false);
      setTryouts([]);
      setDataTryoutsEmpty(true);
      setNoResultsFromSearch(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setDataTryoutsEmpty(false);
    setNoResultsFromSearch(false);

    const getPaketData = async () => {
      try {
        const filters = {
          id_jenis_seleksi: selectedIdJenisSeleksi,
          id_tipe_konten: selectedIdTipeKonten,
        };

        if (selectedIdBidang) {
          filters.id_bidang = selectedIdBidang;
        }
        if (debouncedSearchTerm) {
          filters.nama_paket = debouncedSearchTerm;
        }

        const response = await fetchPaket({
          page: 1,
          per_page: 10,
          filters: filters
        });

        if (response.success) {
          if (Array.isArray(response.data) && response.data.length > 0) {
            const sortedData = [...response.data].sort((a, b) => {
              const premiumA = a.is_premium === true || a.is_premium === 1;
              const premiumB = b.is_premium === true || b.is_premium === 1;

              if (!premiumA && premiumB) {
                return -1;
              }
              if (premiumA && !premiumB) {
                return 1;
              }
              return 0;
            });
            setTryouts(sortedData);
            setNoResultsFromSearch(false);
            setDataTryoutsEmpty(false);
          } else {
            setTryouts([]);
            if (debouncedSearchTerm) {
              setNoResultsFromSearch(true);
            } else {
              setDataTryoutsEmpty(true);
            }
          }
        } else {
          setTryouts([]);
          setError(response.message || 'Gagal mengambil data paket dari server.');
        }
      } catch (err) {
        setTryouts([]);
        setError(err.message || 'Terjadi kesalahan jaringan saat mengambil data paket.');
        console.error("Fetch error in PaketDetails:", err);
      } finally {
        setLoading(false);
      }
    };

    getPaketData();
  }, [selectedIdJenisSeleksi, selectedIdTipeKonten, selectedIdBidang, debouncedSearchTerm, idJenisSeleksiNotFound, idTipeKontenNotFound, urlParamsProcessed]);

  const getBadgeColor = (isPremium) => {
    return (isPremium === true || isPremium === 1) ? 'bg-warning-subtle text-warning' : 'bg-success-subtle text-success';
  };

  const getBadgeIcon = (isPremium) => {
    return (isPremium === true || isPremium === 1) ? <FiZap size={10} className="me-1" /> : <FiAward size={10} className="me-1" />;
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleKerjakan = async (paket) => {
    // Navigasi langsung untuk paket GRATIS
     console.log("Klik Kerjakan:", paket.id_paket);
    if (paket.is_premium !== true && paket.is_premium !== 1) {
      const path = paket.id_tipe_konten === 4
        ? `/pembahasan?id_tipe_konten=${idTipeKontenFromUrl}&id_jenis_seleksi=${idJenisSeleksiFromUrl}&id_paket=${paket.id_paket}`
        : `/simulasi-tryout?id_paket=${paket.id_paket}&id_tipe_konten=${paket.id_tipe_konten}&id_jenis_seleksi=${paket.id_jenis_seleksi}`;
      navigate(path);
      return;
    }

    try {
      // Panggil API untuk paket premium
      const response = await fetchPaketDetail(paket.id_paket, idTipeKontenFromUrl, idJenisSeleksiFromUrl);

      if (response.success == false) {
        setModalMessage(response.message || 'Terjadi kesalahan saat memeriksa langganan.');
        setIsModalOpen(true);
        return; // Hentikan eksekusi lebih lanjut
      }
      
      // Jika 'success' adalah true, lanjutkan navigasi.
      const path = paket.id_tipe_konten === 4
        ? `/pembahasan?id_tipe_konten=${paket.id_tipe_konten}&id_jenis_seleksi=${paket.id_jenis_seleksi}&id_paket=${paket.id_paket}`
        : `/simulasi-tryout?id_paket=${paket.id_paket}&id_tipe_konten=${paket.id_tipe_konten}&id_jenis_seleksi=${paket.id_jenis_seleksi}`;
      navigate(path);
      
    } catch (error) {
      // Tangani error jika API tidak bisa diakses atau respons tidak valid
      setModalMessage(error.message || 'Terjadi kesalahan saat memeriksa langganan.');
      setIsModalOpen(true);
    }
  };


  // --- Conditional Renders ---
  if (!urlParamsProcessed || loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
            <Spinner className="ms-auto" color="primary" />
            <span className="ms-2">Memuat data...</span>
          </div>
          <Alert color="info" className="text-center">Memuat daftar paket...</Alert>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
            <Breadcrumbs title="Paket Simulasi" breadcrumbItem="Daftar Paket" />
          </div>
          <Alert color="danger" className="text-center">
            <FiAlertCircle className="me-2" /> Terjadi kesalahan: {error}
          </Alert>
        </Container>
      </div>
    );
  }

  if (idJenisSeleksiNotFound || idTipeKontenNotFound) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
            <Breadcrumbs title="Paket Simulasi" breadcrumbItem="Daftar Paket" />
          </div>
          <Alert color="warning" className="text-center">
            <FiAlertCircle className="me-2" /> Parameter ID Jenis Seleksi atau Tipe Konten tidak ditemukan atau tidak valid di URL.
          </Alert>
        </Container>
      </div>
    );
  }

  if (noResultsFromSearch && searchTerm) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
            <Breadcrumbs title="Paket Simulasi" breadcrumbItem="Daftar Paket" />
          </div>
          <Alert color="info" className="text-center">
            <FiSearch className="me-2" /> Tidak ada hasil ditemukan untuk "{searchTerm}".
          </Alert>
          <div className="text-center mt-3">
            <Button color="secondary" onClick={() => setSearchTerm('')}>
              Hapus Pencarian
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (dataTryoutsEmpty && !searchTerm) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
            <Breadcrumbs title="Paket Simulasi" breadcrumbItem="Daftar Paket" />
          </div>
          <Alert color="info" className="text-center">
            <FiAlertCircle className="me-2" /> Belum ada paket tryout yang tersedia untuk Jenis Seleksi dan Tipe Konten ini.
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-center mb-3">
          <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
            <FiArrowLeft className="me-2" /> Kembali
          </Button>
          <Breadcrumbs title="Paket Simulasi" breadcrumbItem="Daftar Paket" />
        </div>

        <Row className="mb-4">
          <Col lg={6} className="mx-auto">
            <InputGroup className="search-input-group-minimal">
              <InputGroupText className="search-icon-minimal">
                <FiSearch />
              </InputGroupText>
              <input
                type="text"
                className="form-control search-field-minimal"
                placeholder="Cari nama paket..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>

        <Row className="gy-4 mb-5">
          {Array.isArray(tryouts) && tryouts.map((paket) => (
            <Col key={paket.id_paket} xl={4} lg={6} md={6}>
              <Card className="tryout-card-v17 shadow-sm h-100">
                {/* 1. CardBody dengan padding lebih kecil dan position: relative */}
                <CardBody className="p-2 d-flex flex-column justify-content-between" style={{ position: 'relative' }}> 
                  
                  {/* 2. Ikon menggunakan posisi absolut (absolute positioning) */}
                  <img 
                      src={paketASN} 
                      alt="Logo Paket ASN" 
                      style={{ 
                          width: '100px',  // Ukuran ikon yang ringkas
                          height: '100px', 
                          objectFit: 'contain',
                          position: 'absolute', 
                          left: '10px',     // Jarak dari kiri
                          top: '50%',       // Pindah ke 50% dari atas CardBody
                          transform: 'translateY(-50%)', // Pindahkan ke atas setengah tingginya sendiri untuk senter vertikal
                          zIndex: 10 
                      }} 
                  />
                  

                  {/* 3. Konten Judul dan Badge (Dimulai dari offset 3) */}
                  <Row className="g-0 mb-2"> 
                      <Col xs={{ size: 9, offset: 3 }} className="ps-2"> 
                          <div className="d-flex align-items-start justify-content-between flex-wrap">
                              {/* Nama Paket */}
                              <h6 className="fw-bold text-dark tryout-title-v17 mb-0 me-2 text-start" style={{ lineHeight: '1.2' }}>
                                  {paket.nama_paket}
                                  {/* Teks kecil di bawah nama paket (misalnya bidang) */}
                                  <div className="text-muted fw-normal" style={{ fontSize: '0.85rem' }}>
                                      {paket.nama_bidang || "Paket"}
                                  </div>
                              </h6>
                              
                              {/* Badge Premium/Gratis */}
                              <Badge
                                  color=""
                                  className={`badge-tipe-ultrasmall text-uppercase ${getBadgeColor(paket.is_premium)}`}>
                                  {getBadgeIcon(paket.is_premium)}
                                  {(paket.is_premium === true || paket.is_premium === 1) ? 'PREMIUM' : 'GRATIS'}
                              </Badge>
                          </div>
                      </Col>
                  </Row>


                  {/* 4. Bagian WAKTU dan SOAL (Dibuat dua kolom, dimulai dari offset 3) */}
                  {/* Ganti g-0 menjadi gx-2 untuk sedikit ruang horizontal antar kolom info */}
                  <Row className="gx-2 d-flex align-items-start mt-2"> 
                      
                      {/* Kolom Waktu: size 4, offset 3 */}
                      <Col xs={{ size: 4, offset: 3 }} className="text-start ps-2"> 
                          <div className="text-muted fw-normal" style={{ fontSize: '0.8rem' }}>
                              Waktu
                          </div>
                          <div className="text-dark fw-bold" style={{ fontSize: '0.9rem' }}>
                              {paket.durasi_menit != null ? `${paket.durasi_menit} Menit` : '-'}
                          </div>
                      </Col>

                      {/* Kolom Soal: size 4 */}
                  </Row>

                </CardBody>
                <CardFooter className="bg-white border-top text-center p-2">
                  <Button
                    color="primary"
                    className="d-block mx-auto w-100 btn-action-v17"
                    onClick={() => handleKerjakan(paket)}
                  >
                    {paket.id_tipe_konten === 4 ? "Lihat Pembahasan" : "Kerjakan"} <FiArrowRight className="ms-1" />
                  </Button>
                </CardFooter>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} centered>
        <ModalHeader toggle={toggleModal}>Info</ModalHeader>
        <ModalBody className="text-center">
          <FiAlertCircle size={40} className="text-warning mb-3" />
          <p className="lead fw-bold">{modalMessage}</p>
          <p className="text-muted">Untuk mengakses paket premium ini, Anda harus memiliki langganan aktif.</p>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-center">
          <Button color="secondary" onClick={toggleModal}>
            Tutup
          </Button>
        </ModalFooter>
      </Modal>

      {/* Styling */}
      <style>
        {`
          *, *::before, *::after {
            box-sizing: border-box;
          }
          .search-input-group-minimal {
            border-radius: 10px;
            overflow: hidden;
            box-shadow: 0 2px 10px rgba(0,0,0,0.04);
            border: 1px solid #e0e0e0;
          }
          .search-icon-minimal {
            background-color: #ffffff;
            border: none;
            color: #6c757d;
            font-size: 1.1rem;
            padding: 0.75rem 1rem;
          }
          .search-field-minimal:focus {
            box-shadow: none;
            border-color: transparent;
            outline: none;
          }
          .tryout-card-v17 {
            transition: all 0.3s ease-in-out;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.08);
            background-color: #ffffff;
            border: none;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
          .tryout-card-v17:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
          }
          .tryout-title-v17 {
            font-size: 1.15rem;
            font-weight: 700;
            line-height: 1.3;
            color: #343a40;
            white-space: normal;
            word-wrap: break-word;
            max-width: 100%;
            flex-shrink: 1;
          }
          .d-flex.align-items-center.justify-content-center.flex-wrap {
            margin-bottom: 0.5rem !important;
          }
          .badge-tipe-ultrasmall {
            font-size: 0.7rem !important;
            font-weight: 600;
            padding: 0.25em 0.5em !important;
            border-radius: 4px;
            display: inline-flex;
            align-items: center;
            line-height: 1;
            vertical-align: middle;
            letter-spacing: 0.5px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
            transition: all 0.2s ease;
            flex-shrink: 0;
          }
          .badge-tipe-ultrasmall svg {
            font-size: 0.8em;
            margin-right: 0.25em;
          }
          .info-item-small {
            display: flex;
            align-items: center;
            font-size: 0.8rem;
            color: #555;
            white-space: nowrap;
          }
          .info-item-small .info-icon-small {
            font-size: 0.9rem;
            color: #888;
            margin-right: 4px;
          }
          .card-footer {
            background-color: #fcfcfc !important;
            border-top: 1px solid #eeeeee !important;
            padding: 15px !important;
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
          }
          .btn-action-v17 {
            font-size: 0.95rem;
            font-weight: 600;
            padding: 0.6rem 1.2rem;
            border-radius: 8px;
            background-color: #007bff;
            border-color: #007bff;
            transition: all 0.2s ease;
            box-shadow: 0 3px 10px rgba(0,123,255,0.2);
          }
          .btn-action-v17:hover {
            background-color: #0056b3;
            border-color: #0056b3;
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(0,123,255,0.3);
          }
          .btn-action-v17 .ms-1 {
            margin-left: 0.4rem !important;
            font-size: 1rem;
          }
          .h-100 {
            display: flex;
            flex-direction: column;
          }
          @media (max-width: 767.98px) {
            .search-input-group-minimal {
              width: 100%;
            }
            .filter-button-v2 {
                flex: 1 1 auto;
                max-width: 48%;
            }
            .info-item-small {
                font-size: 0.75rem;
            }
            .badge-tipe-ultrasmall {
                font-size: 0.65rem !important;
            }
            .tryout-title-v17 {
                font-size: 1rem;
                max-width: 100%;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PaketDetails;